import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { getISO8601DateString, OpCode, Select } from '@aline/common';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { format, isValid, parseISO } from 'date-fns';
import type { PanelVariableType } from 'src/components';
import {
  AlineDatePicker,
  AlineTooltip,
  useOnChangeFocusVariable,
  useUpdatePrimitiveVariableValue,
  useVariableStyles,
  VariableRowHeader,
} from 'src/components';
import { useGetFieldDisabledReason } from 'src/editor/popovers';
import { domElIdCreators } from 'src/helpers';

interface DateMetadataValueProps extends React.HTMLAttributes<HTMLElement> {
  dateVariable: PanelVariableType;
  documentId: UUID;
  isSelected: boolean;
}

export function DateVariable({
  className,
  dateVariable,
  documentId,
  isSelected,
}: DateMetadataValueProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const { disabledReason } = useGetFieldDisabledReason(dateVariable);
  const [value, setValue] = useState<Date | null>(
    dateVariable.value ? parseISO(dateVariable.value) : null
  );
  const [liEl, setLiEl] = useState<HTMLLIElement | null>(null);
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const { onClickVariable, resetOnBlur, updatePrimitiveVariable } = useUpdatePrimitiveVariableValue(
    dateVariable,
    {
      isFocused,
      syncVariableValue: v => setValue(v.value ? parseISO(v.value) : null),
    }
  );
  const isCompleted =
    typeof dateVariable.value === 'string' || Boolean(dateVariable.selectedAnswer);
  const { classes } = useVariableStyles({ isCompleted, isSelected });

  const { onFocus } = useOnChangeFocusVariable(dateVariable, { inputEl, liEl });

  return (
    <li
      ref={setLiEl}
      aria-selected={isSelected}
      className={clsx(className, classes.variable)}
      data-cy={Select.document.editor.rightDrawer.variable.list.row.container}
      id={domElIdCreators.variableRowEl(dateVariable.nodeId)}
      onClick={onClickVariable}
      onFocus={onFocus}
    >
      <VariableRowHeader
        documentId={documentId}
        isCompleted={isCompleted}
        variable={dateVariable}
      />
      {dateVariable.question && (
        <Typography className={classes.question}>{dateVariable.question}</Typography>
      )}
      <AlineTooltip arrow placement="top" title={disabledReason}>
        <div>
          <AlineDatePicker
            alineInputRef={setInputEl}
            dateFieldProps={{ fullWidth: true }}
            disabled={Boolean(disabledReason)}
            enableFlip={true}
            inputClassName={classes.input}
            inputDataCy={Select.document.editor.rightDrawer.outline.dateFieldRowInput}
            open={open}
            placement="bottom-end"
            setOpen={setOpen}
            value={value}
            onBlur={() => {
              const opCode = resetOnBlur();
              if (opCode === OpCode.Success) setValue(null);
              setIsFocused(false);
            }}
            onDateChange={async date => {
              setValue(date);
              await updatePrimitiveVariable({
                formattedValue: date && isValid(date) ? format(date, 'MMMM d, yyyy') : null,
                value: date && isValid(date) ? getISO8601DateString(date) : null,
              });
            }}
            onFocus={() => setIsFocused(true)}
          />
        </div>
      </AlineTooltip>
    </li>
  );
}
